import {
  Button,
  Page,
  PageBody,
  Result,
  Spinner,
  Stack,
  ToastContainer,
} from '@pypestream/design-system';
import { OnboardingModal } from '@pypestream/ui-patterns';
import * as Sentry from '@sentry/react';
import React, { FC, PropsWithChildren, useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  RouterProvider as ReactRouterProvider,
  Route,
  useNavigate,
  useParams,
  Navigate,
  useMatch,
} from 'react-router-dom';

import { PrimaryNav } from './components';
import { ErrorBoundary } from './components/error-boundaries/error-boundary';
import { OrganizationRoleNames, useAuthRole } from './hooks';
import { IPage } from './pages/types';
import {
  sendManagerEvent,
  sendUserEvent,
  useManagerCtxSelector,
  useUserCtxSelector,
} from './xstate/app.xstate';

import {
  CreateOrganization,
  Homepage,
  LogsPage,
  OrganizationSettings,
  OrganizationsPage,
  ProjectDetails,
  ProjectsPage,
  SuperAdminPage,
  TeamsPage,
  UsersPage,
} from './pages';
import UserDetailsPage from './pages/users/user-details/user-details-page';

import './index.scss';
import { ProjectDetailsTabs } from './pages/projects/project-details/project-details';
import TeamDetailsPage from './pages/teams/team-details/team-details-page';
import { RouteDataProvider } from './render-routes/with-route-data';
import { syncXstateWithRouteParams } from './utils';

export enum ManagerPages {
  HOME = 'Home',
  AUDIT_LOGS = 'AuditLogs',
  MY_ACCOUNT = 'MyAccount',
  ORGANIZATIONS = 'Organizations',
  ORGANIZATION_DETAILS = 'OrganizationDetails',
  CREATE_ORGANIZATION = 'CreateOrganization',
  PROJECTS = 'Projects',
  PROJECT_DETAILS_GENERAL_TAB = 'ProjectDetailsGeneral',
  PROJECT_DETAILS_ENVIRONMENTS_TAB = 'ProjectDetailsEnvironments',
  PROJECT_ENVIRONMENT_DETAILS = 'ProjectEnvironmentDetails',
  APPLET_DETAILS = 'AppletDetails',
  APPLET_PLAYGROUND = 'AppletPlayground',
  SUPER_ADMIN = 'SuperAdmin',
  TEAMS = 'Teams',
  TEAM_DETAILS = 'TeamDetails',
  USERS = 'Users',
  USER_DETAILS = 'UserDetails',
}

export const MainLayout: FC<PropsWithChildren> = () => {
  const { orgId } = useManagerCtxSelector((ctx) => ({
    orgId: ctx.selectedOrgId,
  }));
  const onSuccess = () => {
    sendUserEvent({ type: 'user.refetch' });
    sendManagerEvent({ type: 'manager.refetchUserSettings', orgId });
  };

  const homepageMatch = useMatch('/organization/:org_id');

  useEffect(() => {
    // switch browser icon on navigation home / other pages
    const linkPng: HTMLLinkElement =
      document.querySelector("link[rel='icon'][type='image/png']") ||
      document.createElement('link');
    const linkSvg: HTMLLinkElement =
      document.querySelector("link[rel='icon'][type='image/svg+xml']") ||
      document.createElement('link');
    const linkIco: HTMLLinkElement =
      document.querySelector("link[rel='shortcut icon']") ||
      document.createElement('link');
    const title =
      document.querySelector('title') || document.createElement('title');

    linkPng.type = 'image/png';
    linkPng.rel = 'icon';

    linkSvg.type = 'image/svg+xml';
    linkSvg.rel = 'icon';

    linkIco.rel = 'shortcut icon';

    if (homepageMatch) {
      linkPng.href = `/home-favicon-96x96.png`;
      linkSvg.href = `/home-favicon.svg`;
      linkIco.href = `/home-favicon.ico`;
      title.text = 'Pypestream Home';
    } else {
      linkPng.href = `/favicon-96x96.png`;
      linkSvg.href = `/favicon.svg`;
      linkIco.href = `/favicon.ico`;
      title.text = 'Pypestream Manager';
    }

    document.head.appendChild(linkPng);
    document.head.appendChild(linkSvg);
    document.head.appendChild(linkIco);
    document.head.appendChild(title);
  }, [homepageMatch]);

  return (
    <ErrorBoundary>
      <RouteDataProvider>
        <Page background="alt">
          <PrimaryNav />
          <Outlet />
          <ToastContainer />
          <OnboardingModal onSuccess={onSuccess} />
        </Page>
      </RouteDataProvider>
    </ErrorBoundary>
  );
};

type WithTenancyEnforcementProps = PropsWithChildren & {
  accessFor: IPage['accessFor'];
};

export const WithTenancyEnforcement: FC<WithTenancyEnforcementProps> = ({
  children,
  accessFor,
}) => {
  const haveAccess = useAuthRole(accessFor || []);
  const isProtectedPage = Boolean(accessFor);
  const navigate = useNavigate();
  const { home } = useManagerCtxSelector((ctx) => ctx.routes);
  const isAuthorized = isProtectedPage ? haveAccess : true;

  return (
    <>
      {isAuthorized ? (
        children
      ) : (
        <PageBody background="none">
          <Result
            align="center"
            status="error"
            label="You are not authorized to see this page"
            body=""
          >
            <Stack gutter="xsmall" justifyContent="center" slot="footer">
              <Button onClick={() => navigate(home)} size="large">
                Return to Home
              </Button>
            </Stack>
          </Result>
        </PageBody>
      )}
    </>
  );
};

// https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

// const router = sentryCreateBrowserRouter(reactPages);

export const RouterProvider = () => {
  const {
    org_id: orgIdParam,
    project_id: projectIdParam,
    applet_id: appletIdParam,
    environment_id: environmentIdParam,
    team_id: teamIdParam,
    user_id: userIdParam,
  } = useParams();

  const {
    orgIdCtx,
    projectIdCtx,
    userIdCtx,
    teamIdCtx,
    environmentIdCtx,
    appletIdCtx,
  } = useManagerCtxSelector(
    ({
      selectedOrgId,
      selectedProject,
      selectedUser,
      selectedTeam,
      selectedEnvironment,
      selectedApplet,
    }) => ({
      orgIdCtx: selectedOrgId,
      projectIdCtx: selectedProject,
      userIdCtx: selectedUser,
      teamIdCtx: selectedTeam,
      environmentIdCtx: selectedEnvironment,
      appletIdCtx: selectedApplet,
    })
  );

  const { loggedInUserId, loggedInUserOrgId } = useUserCtxSelector((ctx) => ({
    loggedInUserId: ctx.user?.id,
    loggedInUserOrgId: ctx.user?.defaultAccount?.defaultOrgId,
  }));

  const orgId = orgIdParam || orgIdCtx;
  const projectId = projectIdParam || projectIdCtx;
  const environmentId = environmentIdParam || environmentIdCtx;
  const userId = userIdParam || userIdCtx;
  const teamId = teamIdParam || teamIdCtx;
  const appletId = appletIdParam || appletIdCtx;

  // making two routes for redirect to my account page
  // /organization/:org_id/my-account - old one for compatibility
  // /my-account - new one for simplicity of use in other system parts without any org ids
  const renderMyAccountRoute = (key: string) => (
    <Route
      path="my-account"
      element={
        !!loggedInUserOrgId && !!loggedInUserId ? (
          <Navigate
            to={`/organization/${loggedInUserOrgId}/users/${loggedInUserId}`}
          />
        ) : (
          <Spinner fullpage />
        )
      }
      key={key}
    />
  );

  const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/"
        element={<MainLayout />}
        loader={({ params, request }) => {
          syncXstateWithRouteParams({
            params,
          });
          return null;
        }}
      >
        {renderMyAccountRoute(`${ManagerPages.MY_ACCOUNT}-new`)}
        <Route path="/organization/:org_id">
          <Route index element={<Homepage />} path="" />
          <Route
            path="audit-logs"
            element={
              <WithTenancyEnforcement
                accessFor={[
                  OrganizationRoleNames.ADMIN,
                  OrganizationRoleNames.SUPER_ADMIN,
                ]}
              >
                <LogsPage title="Logs" />
              </WithTenancyEnforcement>
            }
          />
          {renderMyAccountRoute(ManagerPages.MY_ACCOUNT)}
          <Route
            path="super-admin"
            element={
              <WithTenancyEnforcement
                accessFor={[OrganizationRoleNames.SUPER_ADMIN]}
              >
                <SuperAdminPage title="Super Admin" />
              </WithTenancyEnforcement>
            }
          />

          <Route path="child-orgs">
            <Route
              index
              element={<OrganizationsPage title="Organizations" />}
              path=""
            />
            <Route
              path="create"
              element={
                <WithTenancyEnforcement
                  accessFor={[
                    OrganizationRoleNames.ADMIN,
                    OrganizationRoleNames.SUPER_ADMIN,
                  ]}
                >
                  <CreateOrganization title="Create Organization" />
                </WithTenancyEnforcement>
              }
            />
          </Route>

          <Route
            path="edit"
            element={
              <WithTenancyEnforcement
                accessFor={[
                  OrganizationRoleNames.ADMIN,
                  OrganizationRoleNames.SUPER_ADMIN,
                ]}
              >
                <OrganizationSettings title="Organization Settings" />
              </WithTenancyEnforcement>
            }
          />
          <Route
            path="users"
            element={<UsersPage title="Users" />}
            key={ManagerPages.USERS}
          />
          <Route
            path="users/:user_id"
            element={
              <UserDetailsPage userId={userId || ''} orgId={orgId || ''} />
            }
          />

          <Route path="teams">
            <Route index element={<TeamsPage title="Teams" />} path="" />
            <Route
              path=":team_id"
              element={<TeamDetailsPage title="Team Details" />}
            />
          </Route>

          <Route
            path="projects"
            shouldRevalidate={() => {
              return true;
            }}
          >
            <Route index element={<ProjectsPage title="Projects" />} />

            <Route path=":project_id">
              <Route
                element={
                  <ProjectDetails
                    title="Project Details"
                    background="secondary"
                    tab={ProjectDetailsTabs.general}
                  />
                }
                index
              />
              <Route path="environments">
                <Route
                  element={
                    <ProjectDetails
                      title="Project Details"
                      background="secondary"
                      tab={ProjectDetailsTabs.environments}
                      hideDeleteButton
                    />
                  }
                  index
                />
                <Route path=":environment_id">
                  <Route
                    index
                    element={
                      <ProjectDetails
                        title="Project Environment Details"
                        background="secondary"
                        tab={ProjectDetailsTabs.environments}
                        hideDeleteButton
                        key={environmentId}
                        environmentId={environmentId}
                      />
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    )
  );

  return <ReactRouterProvider router={router} />;
};
